import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Sun, Moon, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("tg-theme") === "dark"; } catch { return false; }
  });
  useEffect(() => { localStorage.setItem("tg-theme", dark ? "dark" : "light"); }, [dark]);
  return [dark, setDark];
}

// Defined outside RegisterPage so React never remounts the <input> on re-render
// (defining a component inside another component causes focus loss on every keystroke)
const FormInput = ({ id, type, placeholder, value, onChange, onFocus, onBlur, rightElem, inputBg, inputText, borderColor }) => (
  <div style={{ position: "relative" }}>
    <input
      id={id}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      required
      style={{
        width: "100%", height: 50, borderRadius: 13, border: `2px solid ${borderColor}`,
        background: inputBg, color: inputText, padding: rightElem ? "0 50px 0 16px" : "0 16px",
        fontSize: 15, outline: "none", transition: "border-color 0.2s",
        boxSizing: "border-box", fontFamily: "'Nunito', sans-serif",
      }}
    />
    {rightElem && (
      <div style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)" }}>
        {rightElem}
      </div>
    )}
  </div>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [D, setDark] = useDarkMode();
  const [formData, setFormData] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);

  const pwStrength = (p) => {
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6) s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };
  const strength = pwStrength(formData.password);
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"][strength] || "";
  const strengthColor = ["", "#ef4444", "#f97316", "#eab308", "#22c55e", "#10b981"][strength] || "#e5e7eb";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.error("Passwords don't match!"); return; }
    if (formData.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: formData.name, email: formData.email, password: formData.password }),
      });
      const data = await response.json();
      if (response.ok) {
        login(data.access_token || data.token, data.user);
        toast.success("Welcome to Tax Genius! 🎉");
        navigate("/dashboard");
      } else {
        toast.error(data.detail || "Registration failed");
      }
    } catch {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const bg        = D ? "#0f172a" : "#f0fdf4";
  const panelBg   = D ? "#1e293b" : "#ffffff";
  const textPri   = D ? "#f1f5f9" : "#1e293b";
  const textSec   = D ? "#94a3b8" : "#64748b";
  const inputBg   = D ? "#0f172a" : "#f8fafc";
  const iBorder   = (f) => focused === f ? "#10b981" : D ? "#334155" : "#e2e8f0";
  const inputText = D ? "#f1f5f9" : "#1e293b";
  const pillBg    = D ? "#0f172a" : "#f0fdf4";

  const perks = [
    { e: "🤖", t: "AI questions covering Budget 2025" },
    { e: "⚔️", t: "1v1 real-time tax challenges" },
    { e: "🏆", t: "Leaderboard & certificates" },
    { e: "📊", t: "Personal progress analytics" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: bg, display: "flex", fontFamily: "'Nunito', sans-serif", transition: "background 0.3s" }}>

      {/* ── Left decorative panel ── */}
      <div
        style={{ flex: 1, background: "linear-gradient(145deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "48px 40px", position: "relative", overflow: "hidden" }}
        className="hidden md:flex"
      >
        <div style={{ position: "absolute", width: 380, height: 380, background: "rgba(255,255,255,0.05)", borderRadius: "50%", top: -120, right: -120 }} />
        <div style={{ position: "absolute", width: 200, height: 200, background: "rgba(255,255,255,0.06)", borderRadius: "50%", bottom: 40, left: -60 }} />
        <div style={{ position: "absolute", width: 100, height: 100, background: "rgba(255,255,255,0.08)", borderRadius: "50%", top: "20%", left: "30%" }} />

        <div style={{ position: "relative", textAlign: "center", color: "white", maxWidth: 380 }}>
          <div style={{ fontSize: 64, marginBottom: 12 }}>🏆</div>
          <h1 style={{ fontSize: 36, fontWeight: 900, marginBottom: 10 }}>Join Tax Genius</h1>
          <p style={{ fontSize: 16, opacity: 0.85, lineHeight: 1.7, marginBottom: 40 }}>
            India's smartest platform for learning Income Tax. Free forever.
          </p>

          {/* Perks list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14, textAlign: "left" }}>
            {perks.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 * i }}
                style={{ display: "flex", alignItems: "center", gap: 14, background: "rgba(255,255,255,0.14)", backdropFilter: "blur(10px)", borderRadius: 16, padding: "14px 20px" }}
              >
                <span style={{ fontSize: 24 }}>{p.e}</span>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{p.t}</span>
              </motion.div>
            ))}
          </div>

          {/* Badge */}
          <div style={{ marginTop: 36, display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.18)", borderRadius: 100, padding: "10px 20px", backdropFilter: "blur(10px)" }}>
            <span style={{ fontSize: 18 }}>🔒</span>
            <span style={{ fontSize: 13, fontWeight: 700 }}>100% Free · No Credit Card</span>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{ width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", justifyContent: "center", padding: "40px 36px", background: panelBg, position: "relative", transition: "background 0.3s", boxShadow: D ? "none" : "-8px 0 40px rgba(0,0,0,0.06)", overflowY: "auto" }}>

        {/* Top controls */}
        <div style={{ position: "absolute", top: 24, left: 24, right: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 6, color: textSec, background: "none", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
            <ArrowLeft size={15} /> Back to Home
          </button>
          <button onClick={() => setDark(!D)} style={{ width: 38, height: 38, borderRadius: "50%", background: D ? "#334155" : "#f1f5f9", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {D ? <Sun size={17} color="#fbbf24" /> : <Moon size={17} color="#64748b" />}
          </button>
        </div>

        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ paddingTop: 20 }}>

          {/* Mobile logo */}
          <div className="md:hidden" style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 48 }}>🏆</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: textPri }}>Tax Genius</h2>
          </div>

          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 28, fontWeight: 900, color: textPri, marginBottom: 6, letterSpacing: -0.5 }}>Create your account</h2>
            <p style={{ color: textSec, fontSize: 15 }}>Free forever. No credit card required.</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Name */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: textPri, display: "block", marginBottom: 6 }}>Full Name</label>
              <FormInput
                id="name" type="text" placeholder="Your name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                onFocus={() => setFocused("name")}
                onBlur={() => setFocused(null)}
                inputBg={inputBg} inputText={inputText} borderColor={iBorder("name")}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: textPri, display: "block", marginBottom: 6 }}>Email Address</label>
              <FormInput
                id="email" type="email" placeholder="you@example.com"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused(null)}
                inputBg={inputBg} inputText={inputText} borderColor={iBorder("email")}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: textPri, display: "block", marginBottom: 6 }}>Password</label>
              <FormInput
                id="password" type={showPassword ? "text" : "password"}
                placeholder="Create a password (min 6 chars)"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                onFocus={() => setFocused("password")}
                onBlur={() => setFocused(null)}
                inputBg={inputBg} inputText={inputText} borderColor={iBorder("password")}
                rightElem={
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: textSec, display: "flex", alignItems: "center" }}>
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                }
              />
              {/* Strength bar */}
              {formData.password && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                    {[1, 2, 3, 4, 5].map(i => (
                      <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i <= strength ? strengthColor : D ? "#334155" : "#e5e7eb", transition: "background 0.3s" }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 12, color: strengthColor, fontWeight: 700 }}>{strengthLabel}</span>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: textPri, display: "block", marginBottom: 6 }}>Confirm Password</label>
              <FormInput
                id="confirmPassword" type={showConfirm ? "text" : "password"}
                placeholder="Repeat your password"
                value={formData.confirmPassword}
                onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                onFocus={() => setFocused("confirmPassword")}
                onBlur={() => setFocused(null)}
                inputBg={inputBg} inputText={inputText} borderColor={iBorder("confirmPassword")}
                rightElem={
                  formData.confirmPassword
                    ? formData.password === formData.confirmPassword
                      ? <CheckCircle size={17} color="#10b981" />
                      : (
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: textSec, display: "flex", alignItems: "center" }}>
                          {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      )
                    : null
                }
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              style={{
                width: "100%", height: 54, borderRadius: 14, marginTop: 22,
                background: loading ? "#a78bfa" : "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                color: "white", border: "none", fontWeight: 800, fontSize: 16,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                boxShadow: loading ? "none" : "0 6px 24px rgba(168,85,247,0.35)",
                fontFamily: "'Nunito', sans-serif",
              }}
            >
              {loading ? (
                <>
                  <div style={{ width: 20, height: 20, border: "2.5px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  Creating account...
                </>
              ) : "Create Free Account 🚀"}
            </button>
          </form>

          <div style={{ marginTop: 22, textAlign: "center" }}>
            <p style={{ color: textSec, fontSize: 14 }}>
              Already have an account?{" "}
              <Link to="/login" data-testid="login-link" style={{ color: "#7c3aed", fontWeight: 700, textDecoration: "none" }}>Sign in here</Link>
            </p>
          </div>

          {/* Trust badges */}
          <div style={{ marginTop: 28, background: pillBg, borderRadius: 16, padding: "16px 20px", display: "flex", justifyContent: "space-around", transition: "background 0.3s" }}>
            {[{ e: "🔒", t: "Secure" }, { e: "🆓", t: "Free" }, { e: "🇮🇳", t: "India-focused" }].map((b, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{b.e}</div>
                <div style={{ fontSize: 12, color: textSec, fontWeight: 700 }}>{b.t}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default RegisterPage;
